<template>
  <div class="w-100 h-100">
    <b-container fluid>
      <h2 class="tw-mb-0 text-aegis-dk">
        Analysis Data
      </h2>
      <p class="tw-italic tw-text-gray-700">
        View/Download curated analysis data.
      </p>
    </b-container>

    <b-container fluid class="tw-my-5">
      <b-row>
        <b-col>
          <b-form-select size="lg" v-model="selected_view" :options="views" @change="resetPhase()"></b-form-select>
        </b-col>
        <b-col>
          <!-- <b-form-select :disabled="!selected_view.includes('Primary_First_Time_Infection')" size="lg" v-model="selected_phase" :options="study_phases"></b-form-select> -->
          <b-form-select size="lg" v-model="selected_phase" :options="study_phases"></b-form-select>
        </b-col>

        <b-col
          v-if="totalItems"
          cols="2"
          class="text-right"
        >
          <b-button
            size="lg"
            @click="downloadFile()"
            variant="secondary"
            target="_blank"
            class="px-5"
          >
            {{ csvLoading
                ? "Downloading..."
                : csvLoadingError
                  ? "Error"
                  : "Download"
            }}
          </b-button>
        </b-col>
        <!-- conditional download button for data dictionary D13 -->
        <b-col
          v-if="selected_view === 'D13 - UNITAID Household Coverage Report'"
          cols="2"
          class="text-right"
        >
          <b-button
            size="lg"
            @click="downloadD13Extra"
            variant="secondary"
            class="px-4"
          >
            Data Dictionary
          </b-button>
        </b-col>
        <!-- conditional download button for data dictionary D14 -->
        <b-col
          v-if="selected_view === 'D14 - UNITAID Cluster Coverage Report'"
          cols="2"
          class="text-right"
        >
          <b-button
            size="lg"
            @click="downloadD14Extra"
            variant="secondary"
            class="px-4"
          >
            Data Dictionary
          </b-button>
        </b-col>
      </b-row>
    </b-container>

    <b-container fluid>
      <span class="text-secondary tw-text-xs text-center" v-if="last_update">Last Updated: {{last_update}}</span>

      <!-- add loading/error banner -->
      <b-alert v-if="loading" variant="info" show>Loading... <i class="fas fa-spin fa-spinner"></i></b-alert>
      <b-alert v-if="loadingError" variant="danger" show>Cannot load data <i class="fas fa-spin fa-spinner"></i></b-alert>

      <b-table
        ref="table"
        striped
        hover
        responsive
        bordered
        :items="data"
        :fields="fields"
        :busy.sync="loading"
        :current-page="options.currentPage"
        :per-page="options.perPage"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
      >
        <template v-slot:cell(age_at_enrollment)="data">
            {{parseFloat(data.value).toPrecision(2)}}
        </template>
    </b-table>
    </b-container>

    <b-pagination
      v-if="totalItems"
      v-model="options.currentPage"
      :total-rows="options.totalRows"
      :per-page="options.perPage"
      align="right"
      size="sm"
      class="my-0"
    ></b-pagination>

  </div>
</template>

<script>
import { HTTP } from "@/utilities/http-common";
import HandleErrors from "@/utilities/handle-errors";

import DashboardBaseTemplate from "@/layouts/DashboardBaseTemplate.vue";

import * as _ from "lodash";
import moment from 'moment-timezone';

export default {
  mixins: [DashboardBaseTemplate],
  data() {
    return {
      last_update: null,
      options: {
        currentPage: 1,
        totalRows: 0,
        perPage: 30,
      },
      loading: false,
      csvLoading: false,
      loadingError: false,
      csvLoadingError: false,
      search: "",
      fields: [],
      data: [],
      sortBy: "id",
      sortDesc: false,
      title: "",
      totalItems: 0,
      stacked: false,
      selected_view: "D1.1 - Blood Sample(Longitudinal)",
      views: [
        {
          value: "D1.0 - Luminex and IgG Results",
          text: "D1.0 - Luminex and IgG Results"
        },
        {
          value: "D1.1 - Blood Sample(Longitudinal)",
          text: "D1.1 - Blood Sample(Longitudinal)"
        },
        {
          value: "D1.1A - IgG Results",
          text: "D1.1A - IgG Results"
        },
        {
          value: "D1.1B - T1 Luminex Results",
          text: "D1.1B - T1 Luminex Results"
        },
        {
          value: "D1.1C - T1 nu T1 Luminex Results",
          text: "D1.1C - T1 nu T1 Luminex Results"
        },
        {
          value: "D1.1D - T1 nu T2 Luminex Results",
          text: "D1.1D - T1 nu T2 Luminex Results"
        },
        {
          value: "D1.1E - T2 nu T1 Luminex Results",
          text: "D1.1E - T2 nu T1 Luminex Results"
        },
        {
          value: "D1.1F - T2 nu T2 Luminex Results",
          text: "D1.1F - T2 nu T2 Luminex Results"
        },
        {
          value: "D1.1G - T2 Luminex Results",
          text: "D1.1G - T2 Luminex Results"
        },
        {
          value: "D1.1H - T0 Luminex Results",
          text: "D1.1H - T0 Luminex Results"
        },
        {
          value: "D1.2 - Blood Sample(Febrile)",
          text: "D1.2 - Blood Sample(Febrile)"
        },
        {
          value: "D1.2A - PCR Results",
          text: "D1.2A - PCR Results"
        },
        {
          value: "D2 - Application Rate",
          text: "D2 - Application Rate"
        },
        {
          value: "D2.2 - Application Rate (Cluster)",
          text: "D2.2 - Application Rate (Cluster)"
        },
        {
          value: "D2.3 - Summary Slide",
          text: "D2.3 - Summary Slide"
        },
        {
          value: "D3 - Ento",
          text: "D3 - Ento"
        },
        {
          value: "D4 - Move Forms",
          text: "D4 - Move Forms"
        },
        {
          value: "D5 - Safety",
          text: "D5 - Safety"
        },
        {
          value: "D9 - Coverage",
          text: "D9 - Coverage"
        },
        {
          value: "D12 - Missed Visits",
          text: "D12 - Missed Visits"
        },
        {
          value: "D13 - UNITAID Household Coverage Report",
          text: "D13 - UNITAID Household Coverage Report"
        },
        {
          value: "D14 - UNITAID Cluster Coverage Report",
          text: "D14 - UNITAID Cluster Coverage Report"
        },
      ],
      study_phases: [
        "All",
        "Baseline",
        "Intervention",
        "Product"
      ],
      selected_phase: "All",
      d13DataDictionaryBlob: null,
      d14DataDictionaryBlob: null,
    };
  },
  computed: {},
  watch: {
    selected_view: function() {
      // this.$refs.table.refresh();
      this.find();
      this.options.currentPage = 1;
      this.get_last_updated_at();
     // Fetch D13 data dictionary if D13 is selected
      if (this.selected_view === "D13 - UNITAID Household Coverage Report") {
        this.fetchD13DataDictionary();
      }
     // Fetch D14 data dictionary if D14 is selected
      if (this.selected_view === "D14 - UNITAID Cluster Coverage Report") {
        this.fetchD14DataDictionary();
      }     
    },
    selected_phase: function() {
      this.find();
      this.options.currentPage = 1;
    }
  },
  created() {
    this.find();
    this.get_last_updated_at();
  },
  methods: {
    resetPhase() {
      this.selected_phase = "All"
    },
    find() {
      const that = this;
      that.loading = true;
      let params = {
        analysis: that.selected_view,
      };

      // if (that.selected_view.includes("Primary_First_Time_Infection")) {
      params.study_phase = that.selected_phase;
      // }

      if (that.selected_view !== "D3 - Ento") {

        return HTTP.get("/srilanka/analysis", {
          params: params
        })
        .then((response) => {
          that.loading = false;
          let fields = [];
          _.each(response.data.fields, function(field) {
            fields.push({
              key: field,
              sortable: true,
            });
          });
          that.fields = fields;

          that.data = response.data.data;
          that.options.totalRows = that.data.length;
          that.totalItems = that.data.length;
        })
        .catch((error) => {
          that.loading = false;
          that.loadingError = true;
          HandleErrors.HandleErrors(error);
          // set field and data to be empty
          that.fields = [];
          that.data = [];
          that.options.totalRows = that.data.length;
          that.totalItems = that.data.length;
        });
      } else {
        that.loading = false;
        that.fields = ["Please download the data"];
        that.data = [];
        that.options.totalRows = 0;
        that.totalItems = 1;
      }
    },
    downloadFile: function() {
      const that = this;
      that.csvLoading = true;
      
      // Special handling for D5 - Safety
      if (that.selected_view === "D5 - Safety") {
        var params = new URLSearchParams();
        var request = {
          params: params,
          responseType: "arraybuffer",
        };

        HTTP.get(`/srilanka/download-d5-safety-data`, request)
        .then((response) => {
          var fileURL = window.URL.createObjectURL(
            new Blob([response.data])
          );
          var fileLink = document.createElement("a");
          fileLink.href = fileURL;
          var fileName = "SL-D5 - Safety_Data_updated-9-25-25.xlsx";
          fileLink.setAttribute("download", fileName);
          document.body.appendChild(fileLink);
          fileLink.click();
          that.csvLoading = false;
          // Cleanup
          window.URL.revokeObjectURL(fileURL);
          document.body.removeChild(fileLink);
        })
        .catch((error) => {
          that.csvLoading = false;
          that.csvLoadingError = true;
          HandleErrors.HandleErrors(error);
        });
        return;
      }
      
      // Default behavior for all other D# selections
      let downloadUrl = `${process.env.VUE_APP_AXIOS_BASE_URL}/srilanka/analysis?download=1&analysis=${that.selected_view}`;

      // if (that.selected_view.includes("Primary_First_Time_Infection")) {
      downloadUrl += `&study_phase=${that.selected_phase}`;
      // }

      // Add responseType blob and timeout configuration
      HTTP.get(downloadUrl, {
        responseType: 'blob',
        timeout: 300000, // 5 minute timeout
      }).then((response) => {
        var fileURL = window.URL.createObjectURL(new Blob([response.data]));
        var fileLink = document.createElement('a');
        fileLink.href = fileURL;
        // fileLink.setAttribute('download', `${that.selected_view}.csv`);
        // const now = new Date();
        fileLink.setAttribute('download', "SL-"+`${that.selected_view}_Data_Updated__${this.last_update}.csv`);
        document.body.appendChild(fileLink);
        fileLink.click();
        that.csvLoading = false;
        // Cleanup
        window.URL.revokeObjectURL(fileURL);
        document.body.removeChild(fileLink);
      }).catch((error) => {
        that.csvLoading = false;
        that.csvLoadingError = true;
        HandleErrors.HandleErrors(error);
      });
    },
    get_last_updated_at: function() {
      HTTP.get(`/lib/get_last_updated_at/${this.selected_view}`, {})
        .then((response) => {
          this.last_update = moment(response.data['last_update']).tz(moment.tz.guess());
          console.log(this.last_update);
        }).catch((error) => {
          this.last_update = null;
          HandleErrors.HandleErrors(error);
        });
    },
    fetchD13DataDictionary() {
      let downloadUrl = `${process.env.VUE_APP_AXIOS_BASE_URL}/analysis-files/D13_data_dictionary.json`;
      HTTP.get(downloadUrl, {
        responseType: 'blob',
        timeout: 300000,
      }).then((response) => {
        // Store the blob for later use (e.g., for download button)
        this.d13DataDictionaryBlob = new Blob([response.data]);
      }).catch((error) => {
        HandleErrors.HandleErrors(error);
      });
    },
    fetchD14DataDictionary() {
      let downloadUrl = `${process.env.VUE_APP_AXIOS_BASE_URL}/analysis-files/D14_data_dictionary.json`;
      HTTP.get(downloadUrl, {
        responseType: 'blob',
        timeout: 300000,
      }).then((response) => {
        // Store the blob for later use (e.g., for download button)
        this.d14DataDictionaryBlob = new Blob([response.data]);
      }).catch((error) => {
        HandleErrors.HandleErrors(error);
      });
    },
    downloadD13Extra() {
      let downloadUrl = `${process.env.VUE_APP_AXIOS_BASE_URL}/srilanka/analysis?download=1&analysis=D13_data_dictionary`;
      // const downloadUrl = `${process.env.VUE_APP_AXIOS_BASE_URL}/analysis-files/d13_data_dictionary.csv`;

      HTTP.get(downloadUrl, {
        responseType: 'blob',
        timeout: 300000,
      }).then((response) => {
        const fileURL = window.URL.createObjectURL(new Blob([response.data]));
        const fileLink = document.createElement('a');
        fileLink.href = fileURL;
        fileLink.setAttribute('download', 'd13_data_dictionary.csv');
        document.body.appendChild(fileLink);
        fileLink.click();
        window.URL.revokeObjectURL(fileURL);
        document.body.removeChild(fileLink);
      }).catch((error) => {
        HandleErrors.HandleErrors(error);
      });
    },
    downloadD14Extra() {
      let downloadUrl = `${process.env.VUE_APP_AXIOS_BASE_URL}/srilanka/analysis?download=1&analysis=D14_data_dictionary`;
      // const downloadUrl = `${process.env.VUE_APP_AXIOS_BASE_URL}/analysis-files/d13_data_dictionary.csv`;

      HTTP.get(downloadUrl, {
        responseType: 'blob',
        timeout: 300000,
      }).then((response) => {
        const fileURL = window.URL.createObjectURL(new Blob([response.data]));
        const fileLink = document.createElement('a');
        fileLink.href = fileURL;
        fileLink.setAttribute('download', 'd14_data_dictionary.csv');
        document.body.appendChild(fileLink);
        fileLink.click();
        window.URL.revokeObjectURL(fileURL);
        document.body.removeChild(fileLink);
      }).catch((error) => {
        HandleErrors.HandleErrors(error);
      });
    },
  }
};
</script>

<style></style>
